import React, { useMemo } from 'react';
import Image from 'next/image';
import Card from '@tempcover/bolt-ui/card';
import { LinkButton } from '../LinkButton/LinkButton';
import { CategoryLabel } from '../CategoryLabel/CategoryLabel';
import { ArticleCardInput, ArticleCardVariant } from './ArticleCard.types';
import { host } from '@/config';

export const ArticleCard: React.FC<ArticleCardInput> = ({
  article,
  className,
  variant = ArticleCardVariant.Default,
  tagVariant = 'primary',
}) => {
  const { title, category, heroImage, url, lastUpdated } = article;
  const TITLE_MAX_LENGTH = 50;

  const truncateTitle = (rawTitle: string) => {
    if (rawTitle.length <= TITLE_MAX_LENGTH) return rawTitle;

    let shortTitle = rawTitle.slice(0, TITLE_MAX_LENGTH);
    const splitShortTitle = shortTitle.split(' ');

    if (splitShortTitle.slice(-1).toString().length < 2) {
      shortTitle = rawTitle.slice(0, TITLE_MAX_LENGTH + 2);
    }

    return shortTitle + '...';
  };

  const truncatedTitle = truncateTitle(title);

  const isNewsroom = /^\/news-room\/.+/.test(url);

  const date = useMemo(
    () =>
      lastUpdated
        ? new Intl.DateTimeFormat('en-GB', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          }).format(new Date(lastUpdated))
        : null,
    [lastUpdated]
  );

  const MetaInfo = ({ labelClassName }: { labelClassName?: string }) => {
    if (isNewsroom) {
      return date ? (
        <div className="text-dark/80 py-2 font-semibold">
          <em>{date}</em>
        </div>
      ) : null;
    }

    return category?.value ? (
      <CategoryLabel category={category.value} variant={tagVariant} className={labelClassName} />
    ) : (
      <div role="presentation" className="py-6" />
    );
  };

  if (variant === ArticleCardVariant.TwoColumn) {
    return (
      <Card className={`flex h-full flex-row gap-3 ${className}`}>
        <div className="relative aspect-square w-1/2 shrink-0 overflow-hidden rounded-md">
          {heroImage && (
            <Image
              src={heroImage}
              alt="" // alt shouldn't be required with role="presentation" but next/image enforces it so setting as empty.
              role="presentation"
              fill
              className="object-cover"
              sizes="(max-width: 786px) 100vw, 25vw"
            />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div className="min-w-0">
            <MetaInfo labelClassName="px-4 py-1.5 text-[11px]" />
            <h2 className="tc-heading-xs mt-2 mb-0 text-base">{truncatedTitle}</h2>
          </div>

          <LinkButton label="Go to article" link={`${host}${url}`} size="small" variant="tertiary" />
        </div>
      </Card>
    );
  }

  return (
    <Card className={`${className} flex h-full flex-col gap-4`}>
      <div className="relative aspect-square w-full shrink-0 overflow-hidden rounded-md">
        {heroImage && (
          <Image
            src={heroImage}
            alt="" // alt shouldn't be required with role="presentation" but next/image enforces it so setting as empty.
            role="presentation"
            fill
            className="object-cover"
            sizes="(max-width: 786px) 100vw, 33vw"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col justify-between gap-4">
        <div>
          <MetaInfo />
          <h3 className={`tc-heading-xs mb-0 ${category?.value ? 'mt-3' : 'my-2'}`}>{truncatedTitle}</h3>
        </div>

        <LinkButton label="Go to article" link={`${host}${url}`} size="small" variant="tertiary" />
      </div>
    </Card>
  );
};
